import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Bars3Icon,
  XMarkIcon,
  UserGroupIcon,
  ArrowRightStartOnRectangleIcon,
  CubeIcon,
  ChartBarIcon,
  BoltIcon,
  CogIcon,
  DocumentDuplicateIcon,
  ShareIcon,
  ChartPieIcon,
  FolderIcon,
  CloudArrowUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  AdjustmentsHorizontalIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../../contexts/AuthContext";
import { useSidebar } from "../../contexts/SidebarContext";
import { UserRole, Workflow } from "../../types";
import { useTranslation } from "react-i18next";
import { apiService } from "../../services/apiService";
import { sortByOrder } from "../../utils/orderUtils";

interface ProjectInstance {
  id: number;
  name: string;
  workflowContainerId: number;
  workflowContainerName: string;
  templateWorkflowId: number;
  templateWorkflowName?: string;
  status: string;
}

type NavRefreshDetail = {
  workflows?: boolean;
  projectInstances?: boolean;
};

const NAV_REFRESH_EVENT = "nav:refresh";
const USER_SETTINGS_EVENT = "user:settings-updated";

export const Navbar: React.FC = () => {
  const { t, i18n } = useTranslation(["navigation", "common"]);
  const { isCollapsed, setIsCollapsed } = useSidebar();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const [isProjectsExpanded, setIsProjectsExpanded] = useState(false);
  const [isArchivedProjectsExpanded, setIsArchivedProjectsExpanded] = useState(false);
  const [isWorkflowsExpanded, setIsWorkflowsExpanded] = useState(false);
  const [expandedWorkflows, setExpandedWorkflows] = useState<Record<number, boolean>>({});
  const [expandedArchivedWorkflows, setExpandedArchivedWorkflows] = useState<Record<number, boolean>>({});
  const [projectInstances, setProjectInstances] = useState<ProjectInstance[]>(
    []
  );
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [workflowOrder, setWorkflowOrder] = useState<number[]>([]);
  const [defaultWorkflowOrder, setDefaultWorkflowOrder] = useState<number[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);
  const {
    user,
    logout,
    isAdmin,
    isManager,
    isEmployee,
    canManageWorkflows,
    isPageHidden,
    getWorkflowsLabel,
    hasWorkflowTerminologyOverride,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const langDropdownRef = useRef<HTMLDivElement>(null);
  const isSidebarCollapsed = isCollapsed && !isMobileOpen;


  const workflowsLabel = getWorkflowsLabel();
  const useWorkflowOverride = hasWorkflowTerminologyOverride();
  const hideActivities = isPageHidden("activities");
  const hideForms = isPageHidden("forms");
  const isEmployeeUser = isEmployee();
  const isVisitorUser = user?.role === UserRole.VISITOR;
  const isProjectManagerUser = user?.role === UserRole.PROJECT_MANAGER;
  const isManagerOrAdmin = isAdmin() || isManager();
  const canManageWorkflowItems = canManageWorkflows();

  const orderedWorkflows = React.useMemo(() => {
    const orderToUse =
      workflowOrder.length > 0 ? workflowOrder : defaultWorkflowOrder;
    return sortByOrder(
      workflows,
      orderToUse,
      (workflow) => workflow.workflowContainerId ?? workflow.id
    );
  }, [workflows, workflowOrder, defaultWorkflowOrder]);

  const navigation = isEmployeeUser
    ? [
        // Employee navigation - simplified
        {
          name: t("navigation:dashboard"),
          href: "/dashboard",
          icon: ChartBarIcon,
          submenuType: null,
        },
        {
          name: t("navigation:companyActivities"),
          href: "/company-activities",
          icon: ChartPieIcon,
          submenuType: null,
        },
        {
          name: t("navigation:activeProjects"),
          href: "/project-instances",
          icon: FolderIcon,
          submenuType: "projects" as const,
        },
        {
          name: t("navigation:archivedProjects"),
          href: "/archived-projects",
          icon: FolderIcon,
          submenuType: "archivedProjects" as const,
        },
        {
          name: useWorkflowOverride ? `Mevcut ${workflowsLabel}` : t("navigation:availableWorkflows"),
          href: "/workflows/available",
          icon: ShareIcon,
          submenuType: null,
        },
      ]
    : isVisitorUser
    ? [
        {
          name: t("navigation:activeProjects"),
          href: "/project-instances",
          icon: FolderIcon,
          submenuType: "projects" as const,
        },
        {
          name: t("navigation:archivedProjects"),
          href: "/archived-projects",
          icon: FolderIcon,
          submenuType: "archivedProjects" as const,
        },
      ]
    : [
        // Manager/Admin navigation - full access
        {
          name: t("navigation:dashboard"),
          href: "/dashboard",
          icon: ChartBarIcon,
          submenuType: null,
        },
        ...(isProjectManagerUser
          ? [
              {
                name: t("navigation:companyActivities"),
                href: "/company-activities",
                icon: ChartPieIcon,
                submenuType: null,
              },
            ]
          : []),
        // Manager Dashboard only for Manager and Admin
        ...(isManagerOrAdmin
          ? [
              {
                name: t("navigation:managerDashboard"),
                href: "/manager-dashboard",
                icon: ChartPieIcon,
                submenuType: null,
              },
            ]
          : []),
        {
          name: t("navigation:activeProjects"),
          href: "/project-instances",
          icon: FolderIcon,
          submenuType: "projects" as const,
        },
        {
          name: t("navigation:archivedProjects"),
          href: "/archived-projects",
          icon: FolderIcon,
          submenuType: "archivedProjects" as const,
        },
        ...(canManageWorkflowItems
          ? [
              {
                name: useWorkflowOverride ? workflowsLabel : t("navigation:workflows"),
                href: "/project-workflows",
                icon: CubeIcon,
                submenuType: "workflows" as const,
              },
            ]
          : []),
        // Activities menu - hidden via company settings (manager/admin only)
        ...(isManagerOrAdmin && !hideActivities
          ? [
              {
                name: t("navigation:activities"),
                href: "/activities",
                icon: CogIcon,
                submenuType: null,
              },
            ]
          : []),
        // Forms menu - hidden via company settings (manager/admin only)
        ...(isManagerOrAdmin && !hideForms
          ? [
              {
                name: t("navigation:forms"),
                href: "/forms",
                icon: DocumentDuplicateIcon,
                submenuType: null,
              },
            ]
          : []),
        ...(isManagerOrAdmin
          ? [
              {
                name: t("navigation:googleDriveSettings"),
                href: "/google-drive-settings",
                icon: CloudArrowUpIcon,
                submenuType: null,
              },
            ]
          : []),
        ...(isManagerOrAdmin
          ? [
              {
                name: t("navigation:teamManagement"),
                href: "/team-management",
                icon: UserGroupIcon,
                submenuType: null,
              },
            ]
          : []),
        ...(isManagerOrAdmin
          ? [
              {
                name: t("navigation:settings"),
                href: "/settings",
                icon: AdjustmentsHorizontalIcon,
                submenuType: null,
              },
            ]
          : []),
      ];

  const isActive = (href: string) => {
    return (
      location.pathname === href || location.pathname.startsWith(href + "/")
    );
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setIsLangDropdownOpen(false);
  };

  const currentLanguage = i18n.language || "tr";
  const languageKey = currentLanguage.toLowerCase();
  const languageLabel = languageKey.startsWith("de")
    ? "Deutsch"
    : languageKey.startsWith("en")
    ? "English"
    : "Türkçe";
  const languageShort = languageKey.startsWith("de")
    ? "DE"
    : languageKey.startsWith("en")
    ? "EN"
    : "TR";

  // Load workflow instances when Projects submenu is expanded
  useEffect(() => {
    if (
      isProjectsExpanded &&
      projectInstances.length === 0 &&
      !loadingProjects
    ) {
      loadProjectInstances();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProjectsExpanded]);

  // Load workflows when Workflows submenu is expanded
  useEffect(() => {
    if (isWorkflowsExpanded && workflows.length === 0 && !loadingWorkflows) {
      loadWorkflows();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWorkflowsExpanded]);

  const loadProjectInstances = useCallback(async () => {
    try {
      setLoadingProjects(true);
      const data = await apiService.getWorkflowInstances();
      setProjectInstances(data || []);
    } catch (error) {
      setProjectInstances([]);
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  const loadWorkflows = useCallback(async () => {
    if (isVisitorUser) {
      // Visitors should not load workflow/container metadata; they only see allowed projects.
      setWorkflows([]);
      setDefaultWorkflowOrder([]);
      return;
    }
    try {
      setLoadingWorkflows(true);
      const containers = await apiService.getAllWorkflowContainers();
      setDefaultWorkflowOrder(
        (containers || []).map((container) => container.id)
      );
      const data = await apiService.getWorkflows();
      setWorkflows(data || []);
    } catch (error) {
      setWorkflows([]);
    } finally {
      setLoadingWorkflows(false);
    }
  }, [isVisitorUser]);

  useEffect(() => {
    const handleNavRefresh = (event: Event) => {
      const detail = (event as CustomEvent<NavRefreshDetail>).detail;
      if ((!detail || detail.workflows) && !isVisitorUser) {
        void loadWorkflows();
      }
      if (!detail || detail.projectInstances) {
        void loadProjectInstances();
      }
    };

    window.addEventListener(NAV_REFRESH_EVENT, handleNavRefresh);
    return () => {
      window.removeEventListener(NAV_REFRESH_EVENT, handleNavRefresh);
    };
  }, [loadProjectInstances, loadWorkflows, isVisitorUser]);

  useEffect(() => {
    const loadUserSettings = async () => {
      if (isVisitorUser) {
        return;
      }
      try {
        const settings = await apiService.getUserSettings();
        if (Array.isArray(settings.workflowContainerOrder)) {
          setWorkflowOrder(settings.workflowContainerOrder);
        }
      } catch (error) {
      }
    };

    loadUserSettings();

    const handleSettingsUpdate = (event: Event) => {
      const detail = (event as CustomEvent<{ workflowContainerOrder?: number[] }>).detail;
      if (detail?.workflowContainerOrder) {
        setWorkflowOrder(detail.workflowContainerOrder);
      }
    };

    window.addEventListener(USER_SETTINGS_EVENT, handleSettingsUpdate);
    return () => {
      window.removeEventListener(USER_SETTINGS_EVENT, handleSettingsUpdate);
    };
  }, [isVisitorUser]);

  const handleProjectInstanceClick = (instance: ProjectInstance) => {
    // Proje detay sayfasına git
    if (instance.id) {
      navigate(`/project-instances/${instance.id}`);
    } else {
      navigate("/project-instances");
    }
    setIsMobileOpen(false);
  };

  const handleWorkflowClick = (workflow: Workflow) => {
    // Workflow Overview sayfasına git
    navigate(`/workflows/${workflow.id}`);
    setIsMobileOpen(false);
  };

  const toggleProjectsSubmenu = () => {
    setIsProjectsExpanded(!isProjectsExpanded);
    if (!isProjectsExpanded) {
      if (projectInstances.length === 0) {
        loadProjectInstances();
      }
      if (!isVisitorUser && workflows.length === 0) {
        loadWorkflows();
      }
    }
  };

  const toggleWorkflowsSubmenu = () => {
    setIsWorkflowsExpanded(!isWorkflowsExpanded);
    if (!isWorkflowsExpanded && workflows.length === 0) {
      loadWorkflows();
    }
  };

  const toggleWorkflowExpand = (workflowId: number) => {
    setExpandedWorkflows((prev) => ({
      ...prev,
      [workflowId]: !prev[workflowId],
    }));
  };

  const toggleArchivedWorkflowExpand = (workflowId: number) => {
    setExpandedArchivedWorkflows((prev) => ({
      ...prev,
      [workflowId]: !prev[workflowId],
    }));
  };

  const toggleArchivedProjectsSubmenu = () => {
    setIsArchivedProjectsExpanded(!isArchivedProjectsExpanded);
    if (!isArchivedProjectsExpanded) {
      if (projectInstances.length === 0) {
        loadProjectInstances();
      }
      if (!isVisitorUser && workflows.length === 0) {
        loadWorkflows();
      }
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        langDropdownRef.current &&
        !langDropdownRef.current.contains(event.target as Node)
      ) {
        setIsLangDropdownOpen(false);
      }
    };

    if (isLangDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isLangDropdownOpen]);

  return (
    <>
      {/* Mobile backdrop */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[100] md:hidden transition-opacity"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 shadow-lg border-r border-slate-700 z-[110] transition-all duration-300 ${
          isSidebarCollapsed ? "w-16" : "w-64"
        } ${
          isMobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header with logo and collapse button */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-slate-700 relative">
            {!isSidebarCollapsed && (
              <Link
                to="/dashboard"
                className="flex items-center space-x-2 text-xl font-bold text-white hover:text-blue-300 transition-colors"
              >
                <BoltIcon className="w-8 h-8 text-blue-400" />
                <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent whitespace-nowrap">
                  FlowMind
                </span>
              </Link>
            )}
            {isSidebarCollapsed && (
              <Link
                to="/dashboard"
                className="flex items-center justify-center w-full"
              >
                <BoltIcon className="w-8 h-8 text-blue-400" />
              </Link>
            )}
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className={`hidden md:flex items-center justify-center p-1.5 rounded-lg hover:bg-slate-700 transition-all duration-200 ${
                isSidebarCollapsed ? "absolute -right-3 top-1/2 -translate-y-1/2" : ""
              }`}
              title={isSidebarCollapsed ? "Expand" : "Collapse"}
            >
              {isSidebarCollapsed ? (
                <ChevronRightIcon className="h-5 w-5 text-blue-500" />
              ) : (
                <ChevronLeftIcon className="h-5 w-5 text-blue-500" />
              )}
            </button>
            <button
              onClick={() => setIsMobileOpen(false)}
              className="md:hidden flex items-center justify-center p-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-slate-700 transition-all duration-200"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation items */}
          <nav className="flex-1 overflow-y-auto py-4 px-2">
            <div className="space-y-1">
              {navigation.map((item) => {
                const IconComponent = item.icon;
                const isProjectsItem = item.submenuType === "projects";
                const isArchivedProjectsItem = item.submenuType === "archivedProjects";
                const isWorkflowsItem = item.submenuType === "workflows";
                const isSubmenuItem = item.submenuType !== null;
                // Active state
                const active = isSubmenuItem
                  ? isProjectsItem
                    ? location.pathname.startsWith("/project-instances")
                    : isArchivedProjectsItem
                    ? location.pathname.startsWith("/archived-projects")
                    : isWorkflowsItem
                    ? location.pathname === "/project-workflows" ||
                      location.pathname.startsWith("/workflows/")
                    : false
                  : isActive(item.href);
                const isExpanded = isProjectsItem
                  ? isProjectsExpanded
                  : isArchivedProjectsItem
                  ? isArchivedProjectsExpanded
                  : isWorkflowsItem
                  ? isWorkflowsExpanded
                  : false;
                const submenuItems = isProjectsItem
                  ? projectInstances.filter(inst => inst.status !== "ARCHIVED")
                  : isArchivedProjectsItem
                  ? projectInstances.filter(inst => inst.status === "ARCHIVED")
                  : isWorkflowsItem
                  ? orderedWorkflows
                  : [];
                const isLoading = isProjectsItem || isArchivedProjectsItem
                  ? loadingProjects
                  : isWorkflowsItem
                  ? loadingWorkflows
                  : false;

                if (isSidebarCollapsed && isSubmenuItem) {
                  // Collapsed sidebar
                  const targetHref = isProjectsItem
                    ? "/project-instances"
                    : isArchivedProjectsItem
                    ? "/archived-projects"
                    : "/workflows";
                  return (
                    <Link
                      key={item.name}
                      to={targetHref}
                      onClick={() => setIsMobileOpen(false)}
                      className={`${
                        active
                          ? "bg-slate-700 text-blue-300"
                          : "text-slate-300 hover:bg-slate-700 hover:text-white"
                      } justify-center flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group relative`}
                      title={item.name}
                    >
                      <IconComponent className="w-5 h-5 flex-shrink-0" />
                      {active && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-blue-400 rounded-r" />
                      )}
                    </Link>
                  );
                }

                if (!isSubmenuItem) {
                  // Regular navigation item
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setIsMobileOpen(false)}
                      className={`${
                        active
                          ? "bg-slate-700 text-blue-300"
                          : "text-slate-300 hover:bg-slate-700 hover:text-white"
                      } ${
                        isSidebarCollapsed
                          ? "justify-center"
                          : "border-l-4 " +
                            (active ? "border-blue-400" : "border-transparent")
                      } flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group relative`}
                      title={isSidebarCollapsed ? item.name : undefined}
                    >
                      <IconComponent
                        className={`${
                          isSidebarCollapsed ? "w-5 h-5" : "w-5 h-5 mr-3"
                        } flex-shrink-0`}
                      />
                      {!isSidebarCollapsed && <span>{item.name}</span>}
                      {isSidebarCollapsed && active && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-blue-400 rounded-r" />
                      )}
                    </Link>
                  );
                }

                // Submenu item (Projects or Workflows)
                return (
                  <div key={item.name} className="space-y-1">
                    <div className="flex items-center">
                      <Link
                        to={
                          isProjectsItem
                            ? "/project-instances"
                            : isArchivedProjectsItem
                            ? "/archived-projects"
                            : "/workflows"
                        }
                        onClick={() => {
                          setIsMobileOpen(false);
                        }}
                        className={`${
                          active
                            ? "bg-slate-700 text-blue-300"
                            : "text-slate-300 hover:bg-slate-700 hover:text-white"
                        } flex items-center flex-1 min-w-0 px-3 py-2.5 rounded-l-lg text-sm font-medium transition-all duration-200 ${
                          active
                            ? "border-l-4 border-blue-400"
                            : "border-l-4 border-transparent"
                        }`}
                      >
                        <IconComponent className="w-5 h-5 mr-3 flex-shrink-0" />
                        <span className="truncate">{item.name}</span>
                      </Link>
                      <button
                        onClick={() => {
                          if (isProjectsItem) {
                            toggleProjectsSubmenu();
                          } else if (isArchivedProjectsItem) {
                            toggleArchivedProjectsSubmenu();
                          } else if (isWorkflowsItem) {
                            toggleWorkflowsSubmenu();
                          }
                        }}
                        className={`${
                          active
                            ? "bg-slate-700 text-blue-300"
                            : "text-slate-300 hover:bg-slate-700 hover:text-white"
                        } px-2 py-2.5 rounded-r-lg transition-all duration-200 flex-shrink-0`}
                      >
                        <ChevronDownIcon
                          className={`w-4 h-4 transition-transform duration-200 ${
                            isExpanded ? "transform rotate-180" : ""
                          }`}
                        />
                      </button>
                    </div>
                    {isExpanded && (
                      <div className="ml-4 space-y-0.5">
                        <div
                          className={`max-h-[420px] overflow-y-auto sidebar-submenu-scroll ${
                            submenuItems.length > 15 ? "pr-2" : ""
                          }`}
                          style={{
                            scrollbarWidth: "thin",
                            scrollbarColor: "#475569 #1e293b",
                          }}
                        >
                          {isLoading ? (
                            <div className="px-3 py-2 text-xs text-slate-400">
                              {t("common:loading")}...
                            </div>
                          ) : submenuItems.length === 0 ? (
                            <div className="px-3 py-2 text-xs text-slate-400">
                              {isProjectsItem
                                ? t("projects:noProjects")
                                : isArchivedProjectsItem
                                ? t("projects:noArchivedProjects")
                                : isWorkflowsItem
                                ? t("workflows:noWorkflows")
                                : t("common:noData")}
                            </div>
                          ) : isProjectsItem || isArchivedProjectsItem ? (
                            // Nested dropdown for Projects (Workflow -> Instances)
                            (() => {
                              const instances = submenuItems as ProjectInstance[];
                              const workflowsForMenu: Array<{ id: number; name: string }> =
                                isVisitorUser
                                  ? Array.from(
                                      instances.reduce((acc, inst) => {
                                        const id = Number(inst.templateWorkflowId);
                                        if (!Number.isFinite(id)) return acc;
                                        if (!acc.has(id)) {
                                          const name =
                                            (inst as any).templateWorkflowName ||
                                            `Workflow ${id}`;
                                          acc.set(id, String(name || "").trim() || `Workflow ${id}`);
                                        }
                                        return acc;
                                      }, new Map<number, string>())
                                    ).map(([id, name]) => ({ id, name }))
                                  : orderedWorkflows.map((w) => ({
                                      id: w.id,
                                      name: w.name || t("common:unnamed"),
                                    }));

                              // If workflow metadata is not available (e.g. visitor), fall back to a flat list.
                              if (workflowsForMenu.length === 0) {
                                return instances.map((instance) => (
                                  <button
                                    key={instance.id}
                                    onClick={() => handleProjectInstanceClick(instance)}
                                    className="text-slate-400 hover:text-slate-200 hover:bg-slate-700/30 w-full text-left px-3 py-1.5 rounded text-xs transition-all duration-200 truncate"
                                    title={instance.workflowContainerName || t("common:unnamed")}
                                  >
                                    {instance.workflowContainerName || t("common:unnamed")}
                                  </button>
                                ));
                              }

                              return workflowsForMenu.map((workflow) => {
                                const isWorkflowExpanded = isArchivedProjectsItem
                                  ? expandedArchivedWorkflows[workflow.id]
                                  : expandedWorkflows[workflow.id];
                                const workflowInstances = instances.filter(
                                  (instance) =>
                                    Number(instance.templateWorkflowId) === workflow.id
                                );
                                const hasInstances = workflowInstances.length > 0;

                                const navigateUrl = isArchivedProjectsItem
                                  ? `/archived-projects?workflowId=${workflow.id}`
                                  : `/project-instances?workflowId=${workflow.id}`;

                                return (
                                  <div key={workflow.id} className="space-y-0.5">
                                    {/* Workflow Level */}
                                    <div className="flex items-center">
                                      <button
                                        onClick={() => {
                                          navigate(navigateUrl);
                                          setIsMobileOpen(false);
                                        }}
                                        className="flex-1 text-left px-3 py-1.5 rounded-l text-xs font-medium text-slate-300 hover:text-slate-100 hover:bg-slate-700/30 transition-all duration-200 truncate"
                                        title={workflow.name || t("common:unnamed")}
                                      >
                                        <span className="truncate">
                                          {workflow.name || t("common:unnamed")}
                                        </span>
                                      </button>

                                      <button
                                        onClick={() => {
                                          if (!hasInstances) return;
                                          if (isArchivedProjectsItem) {
                                            toggleArchivedWorkflowExpand(
                                              workflow.id
                                            );
                                          } else {
                                            toggleWorkflowExpand(workflow.id);
                                          }
                                        }}
                                        disabled={!hasInstances}
                                        className={`px-2 py-1.5 rounded-r text-xs text-slate-300 transition-all duration-200 flex-shrink-0 ${
                                          hasInstances
                                            ? "hover:text-slate-100 hover:bg-slate-700/30"
                                            : "cursor-default opacity-40"
                                        }`}
                                      >
                                        <ChevronDownIcon
                                          className={`w-3.5 h-3.5 transition-transform duration-200 ${
                                            isWorkflowExpanded && hasInstances
                                              ? "transform rotate-180"
                                              : ""
                                          }`}
                                        />
                                      </button>
                                    </div>

                                    {isWorkflowExpanded && hasInstances && (
                                      <div className="ml-3 space-y-0.5">
                                        {workflowInstances.map((instance) => {
                                          const urlParts =
                                            location.pathname.split("/");
                                          const currentIdFromUrl = urlParts[2];
                                          const isInstanceActive =
                                            (location.pathname.startsWith(
                                              "/project-instances/"
                                            ) ||
                                              location.pathname.startsWith(
                                                "/archived-projects/"
                                              )) &&
                                            currentIdFromUrl ===
                                              String(instance.id);

                                          return (
                                            <button
                                              key={instance.id}
                                              onClick={() =>
                                                handleProjectInstanceClick(
                                                  instance
                                                )
                                              }
                                              className={`${
                                                isInstanceActive
                                                  ? "text-blue-300 bg-slate-700/50"
                                                  : "text-slate-400 hover:text-slate-200 hover:bg-slate-700/30"
                                              } w-full text-left px-3 py-1.5 rounded text-xs transition-all duration-200 truncate`}
                                              title={
                                                instance.workflowContainerName ||
                                                t("common:unnamed")
                                              }
                                            >
                                              {instance.workflowContainerName ||
                                                t("common:unnamed")}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              });
                            })()
                          ) : (
                            // Flat list for Workflows (keep existing behavior)
                            submenuItems.map((subItem: any) => {
                              const subItemId = subItem.id;
                              const subItemName = subItem.name || t("common:unnamed");

                              const urlParts = location.pathname.split("/");
                              const currentIdFromUrl = urlParts[2];

                              const isSubItemActive =
                                location.pathname === `/project-workflows/${subItemId}` ||
                                location.pathname === `/workflows/${subItemId}`;

                              return (
                                <button
                                  key={subItemId}
                                  onClick={() => handleWorkflowClick(subItem)}
                                  className={`${
                                    isSubItemActive
                                      ? "text-blue-300 bg-slate-700/50"
                                      : "text-slate-400 hover:text-slate-200 hover:bg-slate-700/30"
                                  } w-full text-left px-3 py-1.5 rounded text-xs transition-all duration-200 truncate`}
                                  title={subItemName}
                                >
                                  {subItemName}
                                </button>
                              );
                            })
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </nav>

          {/* Footer with user info */}
          <div className="border-t border-slate-700 p-4">
            {!isSidebarCollapsed ? (
              <>
                <div className="flex items-center space-x-3 mb-3">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                      <span className="text-sm font-bold text-white">
                        {user?.firstName?.charAt(0)}
                        {user?.lastName?.charAt(0)}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white truncate">
                      {user?.firstName} {user?.lastName}
                    </div>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-sm mt-1">
                      {user?.role}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="relative flex-1" ref={langDropdownRef}>
                    <button
                      onClick={() =>
                        setIsLangDropdownOpen(!isLangDropdownOpen)
                      }
                      className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-700 transition-all duration-200 text-sm"
                    >
                      <span className="flex items-center space-x-2">
                        <span>{languageShort}</span>
                        <span>{languageLabel}</span>
                      </span>
                      <ChevronDownIcon className="h-4 w-4" />
                    </button>
                    {isLangDropdownOpen && (
                      <div className="absolute bottom-full left-0 right-0 mb-2 bg-slate-800 rounded-lg shadow-lg border border-slate-700 z-50">
                        <button
                          onClick={() => changeLanguage("tr")}
                          className={`w-full flex items-center space-x-2 px-4 py-2 text-sm rounded-t-lg transition-colors ${
                            currentLanguage === "tr"
                              ? "bg-slate-700 text-blue-300"
                              : "text-slate-300 hover:bg-slate-700 hover:text-white"
                          }`}
                        >
                          <span>TR</span>
                          <span>Türkçe</span>
                        </button>
                        <button
                          onClick={() => changeLanguage("en")}
                          className={`w-full flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                            currentLanguage === "en"
                              ? "bg-slate-700 text-blue-300"
                              : "text-slate-300 hover:bg-slate-700 hover:text-white"
                          }`}
                        >
                          <span>EN</span>
                          <span>English</span>
                        </button>
                        <button
                          onClick={() => changeLanguage("de")}
                          className={`w-full flex items-center space-x-2 px-4 py-2 text-sm rounded-b-lg transition-colors ${
                            currentLanguage === "de"
                              ? "bg-slate-700 text-blue-300"
                              : "text-slate-300 hover:bg-slate-700 hover:text-white"
                          }`}
                        >
                          <span>DE</span>
                          <span>Deutsch</span>
                        </button>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={logout}
                    className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-all duration-200"
                    title={t("common:logout")}
                  >
                    <ArrowRightStartOnRectangleIcon className="h-5 w-5" />
                  </button>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center space-y-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                  <span className="text-sm font-bold text-white">
                    {user?.firstName?.charAt(0)}
                    {user?.lastName?.charAt(0)}
                  </span>
                </div>
                <div className="relative" ref={langDropdownRef}>
                  <button
                    onClick={() =>
                      setIsLangDropdownOpen(!isLangDropdownOpen)
                    }
                    className="p-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-700 transition-all duration-200"
                    title={languageLabel}
                  >
                    <span className="text-lg">{languageShort}</span>
                  </button>
                  {isLangDropdownOpen && (
                    <div className="absolute bottom-full left-0 mb-2 bg-slate-800 rounded-lg shadow-lg border border-slate-700 z-50 w-32">
                      <button
                        onClick={() => changeLanguage("tr")}
                        className={`w-full flex items-center space-x-2 px-4 py-2 text-sm rounded-t-lg transition-colors ${
                          currentLanguage === "tr"
                            ? "bg-slate-700 text-blue-300"
                            : "text-slate-300 hover:bg-slate-700 hover:text-white"
                        }`}
                      >
                        <span>TR</span>
                        <span>Türkçe</span>
                      </button>
                      <button
                        onClick={() => changeLanguage("en")}
                        className={`w-full flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                          currentLanguage === "en"
                            ? "bg-slate-700 text-blue-300"
                            : "text-slate-300 hover:bg-slate-700 hover:text-white"
                        }`}
                      >
                        <span>EN</span>
                        <span>English</span>
                      </button>
                      <button
                        onClick={() => changeLanguage("de")}
                        className={`w-full flex items-center space-x-2 px-4 py-2 text-sm rounded-b-lg transition-colors ${
                          currentLanguage === "de"
                            ? "bg-slate-700 text-blue-300"
                            : "text-slate-300 hover:bg-slate-700 hover:text-white"
                        }`}
                      >
                        <span>DE</span>
                        <span>Deutsch</span>
                      </button>
                    </div>
                  )}
                </div>
                  <button
                    onClick={logout}
                    className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-all duration-200"
                  title={t("common:logout")}
                >
                  <ArrowRightStartOnRectangleIcon className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Mobile menu button */}
      <button
        onClick={() => setIsMobileOpen(true)}
        className="fixed top-4 left-4 z-[90] md:hidden bg-slate-800 inline-flex items-center justify-center p-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 transition-all duration-200 shadow-lg"
      >
        <Bars3Icon className="block h-6 w-6" />
      </button>
    </>
  );
};






